import { Component } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { observer, inject, Provider } from 'mobx-react'
import { types, flow, getEnv } from 'mobx-state-tree'

import { PageLoading, ErrorPage } from '../../components'
import { TopNav, MainWrapper, MainLayout, MainSection, MainContent } from './components'
import { SideBar } from './components/sidebar'
import { Demo } from './containers'
import Accounts from '../accounts'
import Home from '../home/Home'
import Overall from '../overall-strategy'
import Profile from '../profile'
import Goals from '../goals'
import TransactionConfirmed from '../offboarding/transactions/TransactionConfirmed'
import UpdateContributionsOnly from '../offboarding/transactions/UpdateContributionsOnly'
import SpendDownDashboard from '../spend-down/Dashboard'
import SocialSecurityIncome from '../social-security/SocialSecurityDashboard'

const { model, enumeration } = types

const MainState = model({
  status: enumeration(['loading', 'done', 'error']),
  sidebarActive: true,
  strategyActionQueue: enumeration(['edit', 'compare', '']),
  enableSidebar: true,
  enableTopNav: true,
}).actions(self => ({
  afterCreate: flow(function* () {
    const store = getEnv(self).store
    const {
      features,
      getFeatures,
      getAccount,
      getConfig,
      getCommunicationPreferences,
      getPerson,
      getSpouse,
      getDependents,
      getContactInfo,
      getGoals,
      getAccounts,
      getInstitutionalAccount,
      initializeActiveAccount,
      getAllPensions,
      getUserSettings,
      getPlanOverview,
      getExistingPlaidLinks,
      getSSBenefits,
      getAiStatus,
      getSubscriptions,
    } = store

    try {
      // Members without plan access can't load plan data, so only initialize stores used by their available routes.
      if (!store.hasPlanAccess) {
        yield Promise.all([
          getFeatures(),
          getAccount(),
          getConfig(),
          getPerson(),
          getContactInfo(),
          getCommunicationPreferences(),

          // Distinguishes never-paid from lapsed members; don't block startup if it fails.
          getSubscriptions().catch(err => console.error(err)),

          // Handles its own errors, leaving the chatbot hidden if the AI service is unavailable.
          getAiStatus(),
        ])
        self.status = 'done'
        return
      }

      if (features.enablePlaidLink) {
        try {
          yield getExistingPlaidLinks()
        } catch (err) {
          console.error(err)
        }
      }

      yield Promise.all([
        getFeatures(),
        getAccount(),
        getConfig(),
        getCommunicationPreferences(),
        getPerson(),
        getSpouse(),
        getDependents(),
        getContactInfo(),
        getUserSettings(),
        getAiStatus(),
        getGoals(),
        getAccounts(),
        getInstitutionalAccount(),
        initializeActiveAccount(),
        getAllPensions(),
        getPlanOverview(),
        getSSBenefits(),
      ])

      self.status = 'done'
    } catch (err) {
      console.error(err)
      self.status = 'error'
    }
  }),
  toggleSidebar() {
    self.sidebarActive = !self.sidebarActive
  },
  expandSidebar() {
    self.sidebarActive = true
  },
  setDrawer(value) {
    self.strategyActionQueue = value
  },
  setSidebar(value) {
    self.enableSidebar = value
  },
  setTopNav(value) {
    self.enableTopNav = value
  },
}))

class Main extends Component {
  constructor(props) {
    super(props)
    const { store } = props
    this.state = MainState.create({ status: 'loading', strategyActionQueue: '' }, { store })
  }

  render() {
    const { features, hasPlanAccess } = this.props.store
    const EmptySpan = () => <span />
    const defaultRoute = features.enablePersonalizedTargetDatePlus ? '/overall' : '/home'

    if (this.state.status === 'loading') {
      return <PageLoading />
    }

    if (this.state.status === 'error') {
      return <ErrorPage />
    }

    return (
      <Provider main={this.state}>
        <MainWrapper>
          <MainLayout>
            <Routes>
              <Route path='/change-contribution' element={<EmptySpan />} />
              <Route path='/discontinue' element={<EmptySpan />} />
              <Route path='/transaction-confirmed' element={<EmptySpan />} />
              <Route path='/update-contributions-only' element={<EmptySpan />} />
              <Route path='*' element={<TopNav />} />
            </Routes>
            <MainSection>
              <Routes>
                <Route path='/accounts/add/' element={<EmptySpan />} />
                <Route path='/accounts/:type/:id/investments' element={<EmptySpan />} />
                <Route path='/accounts/:type/:id/investments/add-wizard' element={<EmptySpan />} />
                <Route path='/change-contribution' element={<EmptySpan />} />
                <Route path='/discontinue' element={<EmptySpan />} />
                <Route path='/transaction-confirmed' element={<EmptySpan />} />
                <Route path='/update-contributions-only' element={<EmptySpan />} />
                {features.showSideBar && <Route path='*' element={<SideBar />} />}
              </Routes>
              <MainContent id='main-app'>
                <Routes>
                  <Route path='/home' element={<Home />} />
                  <Route path='/profile' element={<Profile />} />
                  <Route path='/transaction-confirmed' element={<TransactionConfirmed />} />
                  <Route path='/update-contributions-only' element={<UpdateContributionsOnly />} />
                  {process.env.REACT_APP_ENABLE_DEMO_ROUTE && (
                    <Route path='/demo' element={<Demo />} />
                  )}
                  {/* Only register these routes when their required plan data is loaded. */}
                  {hasPlanAccess ? (
                    <>
                      <Route path='/spending/*' element={<SpendDownDashboard />} />
                      {features.enableGoals && <Route path='/goals/*' element={<Goals />} />}
                      {features.enableAccounts && (
                        <Route path='/accounts/*' element={<Accounts />} />
                      )}
                      <Route path='/overall' element={<Overall />} />
                      <Route path='/social-security' element={<SocialSecurityIncome />} />
                      <Route path='/' element={<Navigate to={defaultRoute} replace />} />
                      {/* Redirect stale or disabled routes instead of showing an empty content area. */}
                      <Route path='*' element={<Navigate to={defaultRoute} replace />} />
                    </>
                  ) : (
                    <Route path='*' element={<Navigate to='/home' replace />} />
                  )}
                </Routes>
              </MainContent>
            </MainSection>
          </MainLayout>
        </MainWrapper>
      </Provider>
    )
  }
}

export default inject('store')(observer(Main))
